import { getPurchaseDetailsEndpoint } from '@api/fetchers/checkout';
import fetch from '@api/fetchers';
import useFetch from '@hooks/useFetch';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { segment } from '@actions/analytics';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { readCookie } from '@utils/cookies';
import { CHECKOUT_REFERRAL_CODE_APPLIED } from './constants/segment';

export const getTotalAmtBySubscription = (
  subscription = {},
  withCredits = false
) => {
  const taxBreakdown = withCredits ? 'taxBreakdownWithCredits' : 'taxBreakdown';
  return (
    Number(
      subscription[taxBreakdown]?.find((item) => item.label === 'Total')?.amount
    ) || 0
  );
};

const usePurchaseDetails = ({
  subscriptionUid = '',
  referralCode = null
} = {}) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn) || !!readCookie('accessToken');

  const router = useRouter();
  const {
    subscriptionUID = subscriptionUid,
    referral_code: new_referral_code = referralCode,
    goalUID = '',
    credsApplied = ''
  } = router.query;

  const referral_code = referralCode ?? new_referral_code;

  const {
    data: purchaseDetails,
    mutate,
    ...rest
  } = useFetch(
    subscriptionUID ? getPurchaseDetailsEndpoint(subscriptionUID) : null
  );

  useEffect(() => {
    if (isLoggedIn) {
      mutate();
    }
  }, [isLoggedIn, mutate]);

  const [discountedPurchaseDetails, setDiscountedSubscription] = useState();
  const [referralError, setReferralError] = useState('');
  const [validateReferralInProgress, setValidateReferralInProgress] = useState(
    !!referral_code
  );

  const finalPurchaseDetails = useMemo(() => {
    return referral_code && discountedPurchaseDetails
      ? discountedPurchaseDetails
      : purchaseDetails;
  }, [referral_code, discountedPurchaseDetails, purchaseDetails]);

  const eventProps = useCheckoutEventProps(
    goalUID,
    finalPurchaseDetails?.subscription,
    finalPurchaseDetails?.subscription?.childSubscriptions?.length > 1,
    credsApplied
  );

  useEffect(() => {
    if (referral_code) {
      setReferralError('');
      setValidateReferralInProgress(true);
      fetch(
        subscriptionUID
          ? getPurchaseDetailsEndpoint(subscriptionUID, referral_code)
          : null
      )
        .then((data) => {
          if (data?.subscription) {
            setDiscountedSubscription(data);
          } else {
            setReferralError(data?.message || 'Please enter a valid code');
          }
          segment.track(CHECKOUT_REFERRAL_CODE_APPLIED, {
            ...eventProps,
            price: getTotalAmtBySubscription(data?.subscription),
            subscription_price: getTotalAmtBySubscription(
              purchaseDetails?.subscription
            ),
            referral_code,
            is_referral_successful: !!data?.subscription
          });
        })
        .finally(() => {
          setValidateReferralInProgress(false);
        });
    } else {
      setReferralError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referral_code, isLoggedIn, subscriptionUID]);

  return {
    purchaseDetails: finalPurchaseDetails,
    purchaseDetailsWithoutDiscount: purchaseDetails,
    referral_code,
    referralError,
    validateReferralInProgress,
    mutate,
    ...rest
  };
};

export default usePurchaseDetails;
